import { useState } from "react";

import { useAuthContext } from "providers/Auth";
import { getAvatarInitials } from "utils/userUtils";
import { Layout } from "providers/Layout";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonVariant,
  Input
} from "shared/components";
import { ChangePasswordModal } from "./components/ChangePasswordModal";
import { getErrorMessage } from "utils/error";
import { Copy06 } from "untitledui-js-base";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { toast } from "react-toastify";
import { useForm } from "shared/hooks/use-form";
import { validateBrowserSettings } from "./schema";

export const Profile = () => {
  const { auth } = useAuthContext();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const { onSubmit, register } = useForm(
    {
      per_page: localStorage.getItem("per_page") || "10"
    },
    validateBrowserSettings
  );

  return (
    <Layout
      header={
        <>
          <div className="flex items-center justify-between border-b border-alpha-40 px-3 md:px-6 pb-3">
            <BreadCrumbs
              crumbs={[
                {
                  title: "Settings",
                  href: "/settings"
                },
                {
                  title: "Personal Profile",
                  href: "/settings/profile"
                }
              ]}
            />
          </div>
          <div className="p-6">
            <h1 className="text-xl font-bold text-content-1000">
              Personal Profile
            </h1>
          </div>
        </>
      }
    >
      <div className="flex flex-wrap">
        <div className="rounded-lg border border-alpha-100 bg-background-1000 p-6 shadow-xs lg:min-w-[648px]">
          <div className="mb-6 flex items-center gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full border border-alpha-100 bg-primary-50 text-xs font-medium text-primary-500">
              {getAvatarInitials(auth?.name || "")}
            </div>
            <p className="text-base font-bold text-content-1000">
              {auth?.name || ""}
            </p>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="mb-2 text-sm font-medium text-content-300">
                Email Address
              </p>
              <p className="text-sm font-medium text-content-1000">
                {auth?.email || ""}
              </p>
            </div>
            <IconButton
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(String(auth?.email));
                  toast.success("Email is copied");
                } catch (err) {
                  toast.error(getErrorMessage(err));
                }
              }}
              Icon={Copy06}
              className="!stroke-content-200"
              variant={IconButtonVariant.TEXT}
            />
          </div>
          <div className="my-6 border-b border-alpha-40" />
          <div className="flex items-center justify-between">
            <div>
              <p className="mb-2 text-sm font-medium text-content-300">
                Password
              </p>
              <p className="text-sm font-medium">·············</p>
            </div>
            <Button
              size={ButtonSize.SM}
              onClick={() => setChangePasswordOpen(true)}
            >
              Change Password
            </Button>
          </div>

          <div className="my-6 border-b border-alpha-40" />

          <h2 className="text-xl font-bold mb-4">Browser settings</h2>

          <form
            onSubmit={onSubmit({
              onSuccess: (values) => {
                localStorage.setItem("per_page", values.per_page);
                toast.success("Settings saved");

                // Invalidate all caches
                setTimeout(() => {
                  window.location.reload();
                }, 900);
              }
            })}
          >
            <Input
              id="per_page"
              type="number"
              max={50}
              hideOptional
              label="Default items per page in tables"
              {...register("per_page")}
            />

            <Button
              type="submit"
              variant={ButtonVariant.PRIMARY}
              className="mt-4"
            >
              Save
            </Button>
          </form>
        </div>
      </div>
      <ChangePasswordModal
        opened={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </Layout>
  );
};
